import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { apiErrorMessage } from '@/lib/api-error';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';

const SETTING_KEY = 'loan_settings';
const DEFAULT_TTL_DAYS = 7;
const DEFAULT_SENT_TTL_HOURS = 1;
const DEFAULT_FIRST_PAYMENT_DAY = 10;
const DEFAULT_FIRST_PAYMENT_MIN_DAYS = 15;

type FirstPaymentMode = 'plus_month' | 'fixed_day';

interface StoredLoanSettings {
  limit_ttl_days?: number;
  contract_sent_ttl_hours?: number;
  contract_waits_invoice?: boolean;
  first_payment_mode?: FirstPaymentMode;
  first_payment_day?: number;
  first_payment_min_days?: number;
}

/**
 * T224: how long an approved limit is reused without asking the bank again (`platform_settings.loan_settings`).
 * T228: how long a sent contract waits for the customer before it is cancelled.
 * T231: whether the customer's contract (step 8) waits for the approved merchant → Hisob e-invoice (step 7).
 * T236: when the first installment falls due — a month after the contract, or day N at least X days after it.
 * The PUT stores the whole object — every key goes.
 */
export function LoanSettingsCard({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [ttl, setTtl] = useState(String(DEFAULT_TTL_DAYS));
  const [sentTtl, setSentTtl] = useState(String(DEFAULT_SENT_TTL_HOURS));
  const [waitsInvoice, setWaitsInvoice] = useState(false);
  const [mode, setMode] = useState<FirstPaymentMode>('plus_month');
  const [payDay, setPayDay] = useState(String(DEFAULT_FIRST_PAYMENT_DAY));
  const [minDays, setMinDays] = useState(String(DEFAULT_FIRST_PAYMENT_MIN_DAYS));
  const [saving, setSaving] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['/admin/platform-settings/{key}', { params: { path: { key: SETTING_KEY } } }] });

  useEffect(() => {
    const stored = data?.data?.value as StoredLoanSettings | undefined;
    setTtl(String(stored?.limit_ttl_days ?? DEFAULT_TTL_DAYS));
    setSentTtl(String(stored?.contract_sent_ttl_hours ?? DEFAULT_SENT_TTL_HOURS));
    setWaitsInvoice(stored?.contract_waits_invoice === true);
    setMode(stored?.first_payment_mode === 'fixed_day' ? 'fixed_day' : 'plus_month');
    setPayDay(String(stored?.first_payment_day ?? DEFAULT_FIRST_PAYMENT_DAY));
    setMinDays(String(stored?.first_payment_min_days ?? DEFAULT_FIRST_PAYMENT_MIN_DAYS));
  }, [data]);

  const days = Number(ttl);
  const hours = Number(sentTtl);
  const validDays = Number.isInteger(days) && days >= 1 && days <= 90;
  const validHours = Number.isInteger(hours) && hours >= 1 && hours <= 168;
  const day = Number(payDay);
  const gap = Number(minDays);
  const validPayDay = Number.isInteger(day) && day >= 1 && day <= 28;
  const validMinDays = Number.isInteger(gap) && gap >= 1 && gap <= 60;
  // N and X only matter for `fixed_day`, but they are always stored — so they must always be valid.
  const valid = validDays && validHours && validPayDay && validMinDays;

  const save = async () => {
    setSaving(true);
    try {
      const { error } = await apiClient.PUT('/admin/platform-settings/{key}', { params: { path: { key: SETTING_KEY } }, body: {
        value: {
          limit_ttl_days: days,
          contract_sent_ttl_hours: hours,
          contract_waits_invoice: waitsInvoice,
          first_payment_mode: mode,
          first_payment_day: day,
          first_payment_min_days: gap,
        },
      } });
      if (error) throw error;
      toast.success(t('admin_loan.saved'));
      qc.invalidateQueries({ queryKey: ['/admin/platform-settings/{key}'] });
    } catch (e) {
      toast.error(apiErrorMessage(e, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="loan-settings">
      <CardHeader><CardTitle className="text-base">{t('admin_loan.title')}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="py-6 text-center text-muted-foreground"><Loader2 className="inline animate-spin" size={16} /></div>
        ) : (
          <div className="space-y-4">
            <div className="max-w-xs">
              <Label className="text-xs text-muted-foreground mb-1 block">{t('admin_loan.ttl')}</Label>
              <Input
                value={ttl}
                onChange={(e) => setTtl(e.target.value.replace(/[^\d]/g, ''))}
                disabled={!canEdit}
                inputMode="numeric"
                maxLength={2}
                className="font-mono"
                aria-label={t('admin_loan.ttl')}
              />
              <p className="mt-1 text-xs text-muted-foreground">{t('admin_loan.ttl_hint')}</p>
            </div>
            <div className="max-w-xs">
              <Label className="text-xs text-muted-foreground mb-1 block">{t('admin_loan.sent_ttl')}</Label>
              <Input
                value={sentTtl}
                onChange={(e) => setSentTtl(e.target.value.replace(/[^\d]/g, ''))}
                disabled={!canEdit}
                inputMode="numeric"
                maxLength={3}
                className="font-mono"
                aria-label={t('admin_loan.sent_ttl')}
                aria-invalid={!validHours}
              />
              <p className={`mt-1 text-xs ${validHours ? 'text-muted-foreground' : 'text-destructive'}`}>{t('admin_loan.sent_ttl_hint')}</p>
            </div>
            <div className="flex items-start gap-3">
              <Switch
                checked={waitsInvoice}
                onCheckedChange={setWaitsInvoice}
                disabled={!canEdit}
                aria-label={t('admin_loan.waits_invoice')}
              />
              <div>
                <Label className="text-sm">{t('admin_loan.waits_invoice')}</Label>
                <p className="mt-1 text-xs text-muted-foreground">{t('admin_loan.waits_invoice_hint')}</p>
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-xs text-muted-foreground block">{t('admin_loan.first_payment')}</Label>
              <RadioGroup
                value={mode}
                onValueChange={(v) => setMode(v as FirstPaymentMode)}
                disabled={!canEdit}
                aria-label={t('admin_loan.first_payment')}
              >
                {(['plus_month', 'fixed_day'] as const).map((m) => (
                  <div key={m} className="flex items-center gap-2">
                    <RadioGroupItem value={m} id={`first-payment-${m}`} />
                    <Label htmlFor={`first-payment-${m}`} className="text-sm font-normal">{t(`admin_loan.first_payment_${m}`)}</Label>
                  </div>
                ))}
              </RadioGroup>
              {mode === 'fixed_day' && (
                <div className="grid max-w-md gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">{t('admin_loan.first_payment_day')}</Label>
                    <Input
                      value={payDay}
                      onChange={(e) => setPayDay(e.target.value.replace(/[^\d]/g, ''))}
                      disabled={!canEdit}
                      inputMode="numeric"
                      maxLength={2}
                      className="font-mono"
                      aria-label={t('admin_loan.first_payment_day')}
                      aria-invalid={!validPayDay}
                    />
                    <p className={`mt-1 text-xs ${validPayDay ? 'text-muted-foreground' : 'text-destructive'}`}>{t('admin_loan.first_payment_day_hint')}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">{t('admin_loan.first_payment_min_days')}</Label>
                    <Input
                      value={minDays}
                      onChange={(e) => setMinDays(e.target.value.replace(/[^\d]/g, ''))}
                      disabled={!canEdit}
                      inputMode="numeric"
                      maxLength={2}
                      className="font-mono"
                      aria-label={t('admin_loan.first_payment_min_days')}
                      aria-invalid={!validMinDays}
                    />
                    <p className={`mt-1 text-xs ${validMinDays ? 'text-muted-foreground' : 'text-destructive'}`}>{t('admin_loan.first_payment_min_days_hint')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {canEdit ? (
          <Button onClick={save} disabled={saving || isLoading || !valid}>
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} {t('admin_loan.save')}
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">{t('admin_loan.no_access')}</p>
        )}
      </CardContent>
    </Card>
  );
}
